/**
 * LIHL Card View — the shared DOM presentation of a collectible card.
 *
 * CardCanvas (card-canvas.js) owns the card's pixels; CardView owns how a
 * card is SHOWN: retina-sharp canvas sizing, art loading/caching, and the
 * ambient 3D sway + holo foil sheen. Every flat card on the site renders
 * through this component — the admin mint view, the Goonpass card studio,
 * and the collection — so the look is defined exactly once. (The three.js
 * pack-opening stage on cards.html renders CardCanvas textures directly.)
 *
 * The admin build imports a copied module at admin-ui/src/lib/cardView.js
 * (next to cardCanvas.js) — sync both with `cp` after editing, like the
 * renderer.
 *
 * Usage:
 *   var view = CardView.mount(container, card, { ambient: true });
 *   view.update(card);   // re-render with new data
 *   view.destroy();
 *
 * mount() is idempotent per container: mounting again just updates.
 * card = CardCanvas fields (name, description, flavor_text, rarity,
 *        tagged_name, mint_number, image) plus optional image_url — the
 *        view draws instantly without art, then re-draws when the art
 *        loads (cached module-wide).
 *
 * Views render lazily: only cards near the viewport hold a canvas backing
 * store or fetch their art. Far-offscreen views release both (a grid of
 * 100 cards would otherwise pin hundreds of MB of canvases + decoded
 * JPEGs, which crashes the tab on mobile Safari). On small screens the
 * decoded art is also downscaled and the art cache is LRU-bounded.
 * opts = { ambient: false }  ambient adds the idle sway + foil sheen +
 *        shadow (hero presentations; leave off for grid thumbnails).
 *
 * CardView.primeImage(url, image) seeds the art cache (e.g. from a fresh
 * crop) so previews update without waiting on the CDN.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    // Bundled (admin) build — renderer copy lives next to this file.
    module.exports = factory(require('./cardCanvas'));
  } else {
    root.CardView = factory(root.CardCanvas);
  }
}(typeof self !== 'undefined' ? self : this, function (CardCanvas) {
  'use strict';

  var STYLE_ID = 'lihl-card-view-style';
  // Canvas corner radius (56px on 1024x1434) as percentages, so the holo
  // overlay clips to the card corners at any display size.
  var CSS = [
    '.lihl-card-view { position: relative; width: 100%; }',
    '.lihl-card-view__tilt { position: relative; }',
    /* aspect-ratio keeps the layout box when a released (0x0) canvas has
       no intrinsic size, so lazy rendering never shifts the page. */
    '.lihl-card-view canvas { display: block; width: 100%; height: auto; aspect-ratio: ' + CardCanvas.W + ' / ' + CardCanvas.H + '; }',
    '.lihl-card-view__holo {',
    '  position: absolute; inset: 0; pointer-events: none;',
    '  border-radius: 5.4% / 3.9%;',
    '  mix-blend-mode: color-dodge; opacity: 0;',
    '}',
    /* Ambient mode: slow figure-eight sway with the foil sheen drifting on
       the same clock, so the light reads as moving because the card turns. */
    '.lihl-card-view--ambient { perspective: 900px; }',
    '.lihl-card-view--ambient .lihl-card-view__tilt {',
    '  transform-style: preserve-3d; will-change: transform;',
    '  border-radius: 5.4% / 3.9%;',
    '  box-shadow: 0 14px 40px rgba(0,0,0,0.5);',
    '  animation: lihl-card-sway 11s ease-in-out infinite;',
    '}',
    '.lihl-card-view--ambient .lihl-card-view__holo {',
    /* Soft white gloss by default (every card is glossy); rarity foils
       override the colors below to match the 3D holo shader tints. */
    '  --foil-a: rgba(255,255,255,0.14);',
    '  --foil-b: rgba(255,255,255,0.20);',
    '  --foil-c: rgba(255,255,255,0.12);',
    '  opacity: 0.3;',
    /* Narrow band on a large tile => a light streak sweeping the card,
       never a full-card color wash. */
    '  background-image: linear-gradient(115deg,',
    '    transparent 46%, var(--foil-a) 48.5%, var(--foil-b) 50%, var(--foil-c) 51.5%, transparent 54%);',
    '  background-size: 400% 400%;',
    '  animation: lihl-holo-drift 11s ease-in-out infinite;',
    '}',
    '.lihl-card-view--ambient[data-rarity="rare"] .lihl-card-view__holo {',
    '  opacity: 0.5;',
    '  --foil-a: rgba(89,191,255,0.5); --foil-b: rgba(210,240,255,0.4); --foil-c: rgba(120,140,255,0.45);',
    '}',
    '.lihl-card-view--ambient[data-rarity="epic"] .lihl-card-view__holo {',
    '  opacity: 0.55;',
    '  --foil-a: rgba(255,96,214,0.45); --foil-b: rgba(191,128,255,0.4); --foil-c: rgba(64,224,255,0.4);',
    '}',
    '.lihl-card-view--ambient[data-rarity="legendary"] .lihl-card-view__holo {',
    '  opacity: 0.6;',
    '  --foil-a: rgba(255,204,89,0.55); --foil-b: rgba(255,250,230,0.45); --foil-c: rgba(255,150,64,0.45);',
    '}',
    '.lihl-card-view--ambient[data-rarity="mythical"] .lihl-card-view__holo {',
    '  opacity: 0.65;',
    '  --foil-a: rgba(255,90,80,0.55); --foil-b: rgba(255,235,230,0.45); --foil-c: rgba(255,160,64,0.45);',
    '}',
    /* Emboss glint (the "10%" awards treatment): a faint white streak,
       opposite phase from the foil, masked by the face's own luminance so
       it glints off the frame/name/gem. Mask is applied from JS. */
    '.lihl-card-view__glint {',
    '  position: absolute; inset: 0; pointer-events: none;',
    '  border-radius: 5.4% / 3.9%;',
    '  mix-blend-mode: color-dodge; opacity: 0;',
    '}',
    '.lihl-card-view--ambient .lihl-card-view__glint {',
    '  opacity: 0.16;',
    '  background-image: linear-gradient(115deg,',
    '    transparent 46%, rgba(255,255,255,0.5) 48.5%, rgba(255,255,255,0.7) 50%, rgba(255,255,255,0.5) 51.5%, transparent 54%);',
    '  background-size: 400% 400%;',
    '  animation: lihl-holo-drift 11s ease-in-out infinite;',
    '  animation-delay: -5.5s;',
    '}',
    /* Mythical: a slow pulsing light behind the gem (gem sits at 50%,62%
       of the card face). */
    '.lihl-card-view--ambient[data-rarity="mythical"] .lihl-card-view__tilt::after {',
    '  content: ""; position: absolute; pointer-events: none;',
    '  left: 50%; top: 62%; width: 44%; height: 22%;',
    '  transform: translate(-50%, -50%);',
    '  background: radial-gradient(closest-side, rgba(255,86,70,0.55), rgba(255,86,70,0.2) 45%, transparent 75%);',
    '  mix-blend-mode: screen; border-radius: 50%;',
    '  animation: lihl-mythical-pulse 2.6s ease-in-out infinite;',
    '}',
    '@keyframes lihl-mythical-pulse {',
    '  0%, 100% { opacity: 0.3; transform: translate(-50%, -50%) scale(0.88); }',
    '  50% { opacity: 0.85; transform: translate(-50%, -50%) scale(1.1); }',
    '}',
    '@keyframes lihl-card-sway {',
    '  0%   { transform: rotateX(1.6deg) rotateY(-4.5deg); }',
    '  25%  { transform: rotateX(-2.2deg) rotateY(-1.2deg); }',
    '  50%  { transform: rotateX(1.2deg) rotateY(4.5deg); }',
    '  75%  { transform: rotateX(2.4deg) rotateY(1.2deg); }',
    '  100% { transform: rotateX(1.6deg) rotateY(-4.5deg); }',
    '}',
    '@keyframes lihl-holo-drift {',
    '  0%   { background-position: 84% 22%; }',
    '  50%  { background-position: 16% 78%; }',
    '  100% { background-position: 84% 22%; }',
    '}',
    '@media (prefers-reduced-motion: reduce) {',
    '  .lihl-card-view--ambient .lihl-card-view__tilt,',
    '  .lihl-card-view--ambient .lihl-card-view__holo,',
    '  .lihl-card-view--ambient .lihl-card-view__glint,',
    '  .lihl-card-view--ambient[data-rarity="mythical"] .lihl-card-view__tilt::after { animation: none; }',
    '}'
  ].join('\n');

  function injectStyles() {
    if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  // Memory guardrails for small screens: card art originals are 1704x1280
  // JPEGs (~8.7MB each once decoded) and phones show cards at ~480 backing
  // pixels wide at most, so full-size decodes are pure waste there.
  var SMALL_SCREEN = typeof window !== 'undefined' && typeof screen !== 'undefined' &&
    Math.min(screen.width || 1e9, screen.height || 1e9) <= 820;
  var MAX_ART_SIDE = SMALL_SCREEN ? 1024 : 0;     // 0 = keep full size
  var MAX_CACHED_ART = SMALL_SCREEN ? 20 : 0;     // 0 = unbounded

  // Downscale a decoded image into a canvas so the full-size decode can be
  // garbage-collected. drawImage accepts a canvas anywhere an image works.
  function shrinkImage(img) {
    if (!MAX_ART_SIDE || !img || typeof document === 'undefined') return img;
    var longest = Math.max(img.width || 0, img.height || 0);
    if (longest <= MAX_ART_SIDE) return img;
    try {
      var s = MAX_ART_SIDE / longest;
      var c = document.createElement('canvas');
      c.width = Math.max(1, Math.round(img.width * s));
      c.height = Math.max(1, Math.round(img.height * s));
      var ctx = c.getContext('2d');
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, c.width, c.height);
      return c;
    } catch (e) { return img; }
  }

  // Module-wide art cache: url -> Promise<drawable|null>. LRU-bounded on
  // small screens; primed entries (fresh crops not yet on the CDN) are
  // pinned so eviction never swaps them for a stale/missing remote copy.
  var imageCache = {};
  var imagePinned = {};
  var imageOrder = []; // least-recently-used first
  function touchImage(url) {
    var i = imageOrder.indexOf(url);
    if (i !== -1) imageOrder.splice(i, 1);
    imageOrder.push(url);
    if (!MAX_CACHED_ART) return;
    for (var j = 0; imageOrder.length - j > MAX_CACHED_ART && j < imageOrder.length;) {
      var u = imageOrder[j];
      if (imagePinned[u]) { j++; continue; }
      imageOrder.splice(j, 1);
      delete imageCache[u];
    }
  }
  function loadImage(url) {
    if (!url) return Promise.resolve(null);
    if (!imageCache[url]) {
      imageCache[url] = new Promise(function (resolve) {
        var img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = function () { resolve(shrinkImage(img)); };
        img.onerror = function () { resolve(null); };
        img.src = url;
      });
    }
    touchImage(url);
    return imageCache[url];
  }
  function primeImage(url, image) {
    if (!url) return;
    imageCache[url] = Promise.resolve(image || null);
    imagePinned[url] = true;
    touchImage(url);
  }

  // One shared ResizeObserver keeps every view rendered at its display
  // size (retina-sharp, and hidden-tab mounts fix themselves when shown),
  // and one shared IntersectionObserver drives the lazy render lifecycle:
  // views render as they approach the viewport and release their canvas
  // backing store (and art reference) once they're far offscreen.
  var resizeObserver = null;
  var visObserver = null;
  var instanceByEl = typeof WeakMap !== 'undefined' ? new WeakMap() : null;
  function observe(el, inst) {
    if (!instanceByEl) return false;
    instanceByEl.set(el, inst);
    if (typeof ResizeObserver !== 'undefined') {
      if (!resizeObserver) {
        resizeObserver = new ResizeObserver(function (entries) {
          for (var i = 0; i < entries.length; i++) {
            var target = instanceByEl.get(entries[i].target);
            if (target) target._onResize(entries[i].contentRect.width);
          }
        });
      }
      resizeObserver.observe(el);
    }
    if (typeof IntersectionObserver === 'undefined') return false;
    if (!visObserver) {
      visObserver = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var target = instanceByEl.get(entries[i].target);
          if (target) target._onVisibility(entries[i].isIntersecting);
        }
      }, { rootMargin: '600px 0px 600px 0px' });
    }
    visObserver.observe(el);
    return true;
  }
  function unobserve(el) {
    if (resizeObserver) resizeObserver.unobserve(el);
    if (visObserver) visObserver.unobserve(el);
    if (instanceByEl) instanceByEl.delete(el);
  }

  function CardViewInstance(container, card, opts) {
    injectStyles();
    this.opts = opts || {};
    this.container = container;
    this.el = document.createElement('div');
    this.el.className = 'lihl-card-view' + (this.opts.ambient ? ' lihl-card-view--ambient' : '');
    this._tilt = document.createElement('div');
    this._tilt.className = 'lihl-card-view__tilt';
    this.canvas = document.createElement('canvas');
    this._tilt.appendChild(this.canvas);
    this._holo = document.createElement('div');
    this._holo.className = 'lihl-card-view__holo';
    this._tilt.appendChild(this._holo);
    this._glint = document.createElement('div');
    this._glint.className = 'lihl-card-view__glint';
    this._tilt.appendChild(this._glint);
    this.el.appendChild(this._tilt);
    container.appendChild(this.el);
    container._lihlCardView = this;

    this._token = 0;
    this._img = null;
    this._lastWidth = 0;
    this._resizeQueued = false;
    // Lazy lifecycle: with an IntersectionObserver available, nothing
    // renders (and no art downloads) until the view nears the viewport —
    // mounting a 100-card grid costs almost nothing up front. Without one,
    // every view is treated as always-visible (the old behavior).
    var lazy = observe(this.el, this);
    this._visible = !lazy;
    // Drop the browser's default 300x150 backing store until first render.
    if (lazy) this.canvas.width = this.canvas.height = 0;
    this._needsRender = false;
    this._artWanted = false;
    this.update(card);
  }

  CardViewInstance.prototype.update = function (card) {
    this.card = card || {};
    this.el.dataset.rarity = CardCanvas.RARITY[this.card.rarity] ? this.card.rarity : 'common';
    this._token++;
    this._img = this.card.image || null;
    this._render();
    this._maybeLoadArt();
  };

  CardViewInstance.prototype._maybeLoadArt = function () {
    if (this._img || !this.card.image_url) return;
    if (!this._visible) { this._artWanted = true; return; }
    this._artWanted = false;
    var self = this;
    var token = this._token;
    loadImage(this.card.image_url).then(function (img) {
      if (!img || token !== self._token) return;
      self._img = img;
      self._render();
    });
  };

  CardViewInstance.prototype._onVisibility = function (visible) {
    if (visible === this._visible) return;
    this._visible = visible;
    if (!visible) {
      // Far offscreen: free the canvas backing store (the CSS aspect-ratio
      // keeps the layout box) and drop the art reference so the LRU cache
      // can actually reclaim the decoded pixels.
      if (this.canvas.width) this.canvas.width = this.canvas.height = 0;
      if (this._img && !this.card.image) {
        this._img = null;
        this._artWanted = !!this.card.image_url;
      }
      this._needsRender = true;
    } else {
      if (this._needsRender) {
        this._needsRender = false;
        this._render();
      }
      this._maybeLoadArt();
    }
  };

  CardViewInstance.prototype._render = function () {
    if (!this._visible) { this._needsRender = true; return; }
    var cssWidth = this.el.clientWidth || 300;
    var dpr = Math.min((typeof window !== 'undefined' && window.devicePixelRatio) || 1, 2);
    var ratio = this.opts.pixelRatio || Math.min(2, Math.max(0.12, (cssWidth * dpr) / CardCanvas.W));
    this._lastWidth = cssWidth;
    var data = {};
    for (var k in this.card) data[k] = this.card[k];
    data.image = this._img;
    CardCanvas.renderFace(this.canvas, data, { pixelRatio: ratio });
    this._scheduleGlintMask();
  };

  // Emboss glint (ambient mode): a separate faint white streak masked by
  // the rendered face's own luminance, so light glints off the bright
  // details — frame, name, gem. The colored rarity foil layer stays
  // unmasked at full strength; the card back gets the 100% version.
  CardViewInstance.prototype._scheduleGlintMask = function () {
    if (!this.opts.ambient) return;
    var self = this;
    clearTimeout(this._glintTimer);
    this._glintTimer = setTimeout(function () { self._applyGlintMask(); }, 400);
  };

  CardViewInstance.prototype._applyGlintMask = function () {
    if (!this._glint || !this.canvas.width) return;
    try {
      var w = 160, h = Math.round(w * (CardCanvas.H / CardCanvas.W));
      var c = document.createElement('canvas');
      c.width = w; c.height = h;
      var ctx = c.getContext('2d');
      ctx.drawImage(this.canvas, 0, 0, w, h);
      var d = ctx.getImageData(0, 0, w, h);
      for (var i = 0; i < d.data.length; i += 4) {
        var l = d.data[i] * 0.299 + d.data[i + 1] * 0.587 + d.data[i + 2] * 0.114;
        d.data[i] = d.data[i + 1] = d.data[i + 2] = 255;
        d.data[i + 3] = Math.min(255, l * 1.2);
      }
      ctx.putImageData(d, 0, 0);
      var url = c.toDataURL('image/png');
      this._glint.style.webkitMaskImage = 'url(' + url + ')';
      this._glint.style.maskImage = 'url(' + url + ')';
      this._glint.style.webkitMaskSize = '100% 100%';
      this._glint.style.maskSize = '100% 100%';
    } catch (e) { /* tainted canvas etc — unmasked sheen is the fallback */ }
  };

  CardViewInstance.prototype._onResize = function (width) {
    // Re-render only on meaningful size changes (first layout, tab reveal,
    // breakpoint shifts) — not 1px jitter.
    if (!width || this._resizeQueued) return;
    if (this._lastWidth && Math.abs(width - this._lastWidth) / this._lastWidth < 0.12) return;
    var self = this;
    this._resizeQueued = true;
    requestAnimationFrame(function () {
      self._resizeQueued = false;
      self._render();
    });
  };

  CardViewInstance.prototype.destroy = function () {
    this._token++;
    clearTimeout(this._glintTimer);
    unobserve(this.el);
    if (this.el.parentNode) this.el.parentNode.removeChild(this.el);
    if (this.container._lihlCardView === this) delete this.container._lihlCardView;
  };

  function mount(container, card, opts) {
    if (container._lihlCardView) {
      container._lihlCardView.update(card);
      return container._lihlCardView;
    }
    return new CardViewInstance(container, card, opts);
  }

  return { mount: mount, primeImage: primeImage };
}));
